<script lang="ts">
    import { _ } from "svelte-i18n"
    import Index from "../../SEO/Index.svelte"
    import { onMount } from "svelte"
    import { location } from "../../../i18n"
    import ScrollableProductList from "../product/preview/ScrollableProductList.svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import { setActiveQRCode } from "../../../store"

    let code = $state("")
    let isValidating = $state(false)
    let isValid = $state(false)
    let products: Product[] = $state([])
    let error = $state<string | null>(null)
    let qrCodeData: QRCode | null = $state(null)

    // URL-Parameter auslesen
    onMount(() => {
        const urlParams = new URLSearchParams($location?.search || "")
        const codeFromUrl = urlParams.get("code")

        // Auch direkt aus dem Pfad auslesen, z.B. /qr/XXXXXXXX
        const pathParts = $location?.path?.split("/qr/")
        const codeFromPath = pathParts && pathParts.length > 1 ? pathParts[1].split("?")[0] : null

        if (codeFromUrl) {
            code = codeFromUrl
            validateCode()
        } else if (codeFromPath) {
            code = codeFromPath
            validateCode()
        }
    })

    async function validateCode() {
        if (!code || code.trim().length === 0) {
            error = $_("qr.error-empty-code")
            return
        }

        isValidating = true
        error = null

        try {
            const response = await fetch("/api/_actions/validate-qr-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    token: "owshwerNwoa",
                },
                body: JSON.stringify({
                    code: code.trim().toUpperCase(),
                }),
            })

            const data = await response.json()

            if (response.ok && data.valid) {
                isValid = true
                products = data.products || []

                // QR-Code Daten speichern
                if (data.qrCode) {
                    qrCodeData = data.qrCode
                    // Im Store speichern für spätere Verwendung beim Hinzufügen zum Warenkorb
                    setActiveQRCode(qrCodeData)
                }
            } else {
                // Fehlerbehandlung
                if (data.error === "code_not_found") {
                    error = $_("qr.error-code-not-found")
                } else if (data.error === "code_inactive") {
                    error = $_("qr.error-code-inactive")
                } else if (data.error === "code_already_used") {
                    error = $_("qr.error-code-already-used")
                } else if (data.error === "invalid_type") {
                    error = $_("qr.error-invalid-type")
                } else {
                    error = $_("qr.error-unknown")
                }
            }
        } catch (err) {
            console.error("Error validating QR code:", err)
            error = $_("qr.error-network")
        } finally {
            isValidating = false
        }
    }

    function handleSubmit(e: Event) {
        e.preventDefault()
        validateCode()
    }
</script>

<Index
    title="QR-Code Konfiguration – Renz Briefkasten Adelheid"
    metaDescription="Kostenlose Namensschild-Konfiguration für den Renz Briefkasten Adelheid"
/>

<section class="section-type-1 section_padding">
    <div class="container">
        {#if !isValid}
            <!-- Formular zur Code-Eingabe -->
            <div class="qr-code-entry">
                <h1>{$_("qr.title")}</h1>

                <p class="description">
                    {$_("qr.description")}
                </p>

                <form onsubmit={handleSubmit} class="code-form">
                    <label for="qr-code-input" class="form-label">{$_("qr.enter-code")}</label>

                    <div class="form-row">
                        <input
                            id="qr-code-input"
                            type="text"
                            bind:value={code}
                            placeholder={$_("qr.code-placeholder")}
                            disabled={isValidating}
                            class="code-input"
                            maxlength="20"
                        />

                        <button
                            type="submit"
                            class="button primary submit-button"
                            disabled={isValidating || !code.trim()}
                        >
                            {#if isValidating}
                                <BarLoader size="30" color="#fff" unit="px" />
                            {:else}
                                {$_("qr.validate-button")}
                            {/if}
                        </button>
                    </div>

                    {#if error}
                        <div class="error-message" role="alert">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            <span>{error}</span>
                        </div>
                    {/if}
                </form>

                <div class="info-box">
                    <p class="info-title">
                        <strong>{$_("qr.info-title")}</strong>
                    </p>
                    <p>
                        {$_("qr.info-text")}
                    </p>
                </div>
            </div>
        {:else}
            <!-- Erfolgsseite mit Produktlisting -->
            <div class="success-section">
                <div class="success-header">
                    <div class="title-with-icon">
                        <div class="success-icon">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="64"
                                height="64"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h1>{$_("qr.success-title")}</h1>
                    </div>
                    <p class="success-description">
                        {$_("qr.success-description")}
                    </p>
                </div>

                {#if products.length > 0}
                    <div class="products-section">
                        <h2>{$_("qr.unlocked-products")}</h2>
                        <ScrollableProductList {products} />
                    </div>
                {:else}
                    <div class="no-products">
                        <p>{$_("qr.no-products")}</p>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</section>

<style lang="less">
    @import "../../../css/variables.less";

    .qr-code-entry {
        h1 {
            margin-bottom: 1.5rem;
        }

        .description {
            font-size: 1.05rem;
            line-height: 1.6;
            margin-bottom: 2rem;
        }
    }

    .code-form {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 3rem;

        .form-label {
            font-weight: 600;
            font-size: 0.95rem;
            color: var(--grey-900);
            margin-bottom: 0.5rem;
        }

        .form-row {
            display: flex;
            gap: 1rem;
            align-items: stretch;

            @media @max-mobile {
                flex-direction: column;
                align-items: stretch;
            }
        }
    }

    .code-input {
        padding: 0 1rem;
        font-size: 1rem;
        border: 2px solid var(--grey-300);
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        font-family: monospace;
        transition: border-color 0.2s;
        box-sizing: border-box;
        height: 48px;
        line-height: 1;
        max-width: 300px;

        @media @max-mobile {
            max-width: 100%;
        }

        &:focus {
            outline: none;
            border-color: var(--red-100);
        }

        &:disabled {
            background-color: var(--grey-100);
            cursor: not-allowed;
        }

        @media @max-mobile {
            font-size: 1rem;
        }
    }

    .error-message {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background-color: #fee;
        border: 1px solid #fcc;
        border-radius: 4px;
        color: #c33;
        font-size: 0.95rem;

        svg {
            flex-shrink: 0;
        }
    }

    .submit-button {
        height: 48px !important;
        padding: 0 2rem !important;
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        box-sizing: border-box;
        line-height: 1;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        @media @max-mobile {
            width: 100%;
        }
    }

    .info-box {
        margin-top: 3rem;
        padding: 1.5rem;
        background-color: var(--grey-100);
        border-radius: 8px;

        .info-title {
            margin: 0 0 0.5rem 0;
            color: var(--grey-900);
        }

        p:last-child {
            margin: 0;
            color: var(--grey-700);
            line-height: 1.6;
        }
    }

    .success-section {
        .success-header {
            margin-bottom: 3rem;

            .title-with-icon {
                display: flex;
                align-items: center;
                gap: 1rem;
                margin-bottom: 1rem;
            }

            .success-icon {
                width: 48px;
                height: 48px;
                border-radius: 50%;
                background-color: #d4edda;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                svg {
                    width: 28px;
                    height: 28px;
                    color: #28a745;
                }
            }

            h1 {
                color: var(--grey-900);
                margin: 0;
            }

            .success-description {
                font-size: 1.05rem;
                color: var(--grey-700);
                line-height: 1.6;
            }
        }

        .products-section {
            h2 {
                color: var(--grey-900);
                margin-bottom: 2rem;
            }
        }

        .no-products {
            padding: 2rem 0;

            p {
                color: var(--grey-700);
            }
        }
    }

    @media @max-mobile {
        .qr-code-entry {
            h1 {
                font-size: 1.75rem;
            }

            .description {
                font-size: 1rem;
            }
        }

        .success-section {
            .success-header {
                .title-with-icon {
                    h1 {
                        font-size: 1.5rem;
                    }
                }

                .success-icon {
                    width: 40px;
                    height: 40px;

                    svg {
                        width: 24px;
                        height: 24px;
                    }
                }
            }
        }
    }
</style>
